'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { Button, Heading, LayoutContainer, LoadingIndicator, TextPassage } from '@petsmart-ui/sparky';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isLastActiveExpired } from '@libs/core';

import { useCart } from '../../hooks/useCart';
import { logSentryError } from '../../utils';
import {
    clearAuth0FlowNavigation,
    isAuth0InitiatedBackNavigation,
    resolveAuth0HistoryReturnTo,
} from '../../utils/auth0FlowNavigation';
import {
    type Auth0LoginMethod,
    clearPersistedAuth0LoginMethod,
    markAuth0AutoshipEnrollCompleteAuthed,
} from '../../utils/auth0LoginAnalyticsSignal';
import {
    getAuth0LoginRedirectOptions,
    isAuth0RestrictedCheckoutReturnTo,
} from '../../utils/getAuth0LoginRedirectOptions';
import { getAuth0RedirectOrigin } from '../../utils/getAuth0RedirectOrigin';
import { sanitizeSegmentUrl } from '../../utils/sanitize';

import './Auth0LoginRedirect.scss';

type Auth0LoginRedirectProps = Readonly<{
    /** Path to return to after successful login. Defaults to `/`. */
    returnTo?: string;
    /**
     * When true, never skip Universal Login via an existing Auth0 SSO session.
     * Used after inactivity logout so the user must enter credentials again.
     */
    forceLogin?: boolean;
    /** Loading message shown while redirecting. Defaults to sign-in copy. */
    message?: string;
    /** Extra Auth0 authorize params, e.g. `{ screen_hint: 'signup' }`. */
    authorizationParams?: Record<string, string>;
    /**
     * Explicit `login_method` from the checkout interstitial query param.
     * Persist only when this component actually starts Universal Login.
     */
    loginMethod?: Extract<Auth0LoginMethod, 'checkout'>;
    /** Arms the one-time completion event for in-store Autoship enrollment. */
    isInStoreAutoship?: boolean;
    /** Storefront page that sent the user here, restored when browser Back aborts Auth0. */
    flowEntry?: string;
}>;

/**
 * Redirects the user to the Auth0 Universal Login page on mount.
 * Intended to be rendered as the entire body of a sign-in or create-account
 * destination page (`screen_hint: 'signup'`).
 *
 * `returnTo` is stored in Auth0 `appState` and read by `onRedirectCallback`
 * in `Auth0AppProvider`. `Auth0TokenSync` then sends the user back after
 * the `accessToken` cookie is written.
 *
 * Uses `location.replace` (not `assign`) so Safari cannot bounce back to this
 * page instead of completing `/auth/callback`.
 *
 * Authenticated visitors are sent to `returnTo` even if the SDK still has a
 * leftover error. Inactivity re-auth (`requireCredentials`) ignores that error
 * and starts Universal Login with `prompt=login`; an Auth0 Back handoff with no
 * stored flow entry ignores it too. Other unauthenticated Auth0
 * errors are thrown so `error.tsx` can show the universal error page.
 *
 * Auth0 adds `iss` when it hands a stale or revisited transaction to the Login
 * Initiation URI, which is usually browser Back out of a hosted screen. That
 * hop must not look like a page of its own, so the redirect copy stays hidden.
 */
const DEFAULT_REDIRECT_MESSAGE = 'Redirecting to secure sign in...';
const AUTH0_ISSUER_QUERY_PARAM = 'iss';

type Auth0PathRouter = {
    replace: (href: string) => void;
};

function sanitizeAuth0Path(path: string) {
    return sanitizeSegmentUrl(path, getAuth0RedirectOrigin(), { includeSearch: true });
}

function restoreAuth0HistoryIfNeeded({
    searchParams,
    router,
    hasRedirected,
}: {
    searchParams: { get(name: string): string | null } | null;
    router: Auth0PathRouter;
    hasRedirected: { current: boolean };
}): boolean {
    const historyReturnTo = resolveAuth0HistoryReturnTo(searchParams);
    if (!historyReturnTo) {
        return false;
    }

    hasRedirected.current = true;
    router.replace(sanitizeAuth0Path(historyReturnTo));
    return true;
}

function skipAuthenticatedAuth0Login({
    isAuthenticated,
    requireCredentials,
    isAuth0HistoryHandoff,
    returnTo,
    pathname,
    router,
    isInStoreAutoship,
    hasRedirected,
}: {
    isAuthenticated: boolean;
    requireCredentials: boolean;
    /**
     * Browser Back into an Auth0 flow this tab did not start, e.g. after a password
     * reset. That revokes every session, but the SDK cache can still look signed in.
     */
    isAuth0HistoryHandoff: boolean;
    returnTo: string;
    pathname: string | null;
    router: Auth0PathRouter;
    isInStoreAutoship: boolean;
    hasRedirected: { current: boolean };
}): boolean {
    if (!isAuthenticated || requireCredentials || isAuth0HistoryHandoff) {
        return false;
    }

    hasRedirected.current = true;
    clearAuth0FlowNavigation();
    clearPersistedAuth0LoginMethod();
    const safePath = sanitizeAuth0Path(returnTo);
    if (pathname === safePath) {
        return true;
    }

    if (isInStoreAutoship) {
        markAuth0AutoshipEnrollCompleteAuthed();
    }
    router.replace(safePath);
    return true;
}

export function Auth0LoginRedirect({
    returnTo = '/',
    forceLogin = false,
    message = DEFAULT_REDIRECT_MESSAGE,
    authorizationParams,
    loginMethod,
    isInStoreAutoship = false,
    flowEntry,
}: Auth0LoginRedirectProps) {
    const { loginWithRedirect, isAuthenticated, isLoading, error } = useAuth0();
    const { cart, isLoading: isCartLoading } = useCart();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const hasRedirected = useRef(false);
    const [hasError, setHasError] = useState(false);
    // Public-page inactivity never stamps `reauth=1`. Cookie-gated `/sign-in` visits
    // then arrive with only `redirectUrl`. `auth0Reauth` (via `isLastActiveExpired`)
    // is the durable inactivity signal that Auth0 SSO must not silently restore.
    const requireCredentials = forceLogin || isLastActiveExpired();
    const includeCartCategoryParams = isAuth0RestrictedCheckoutReturnTo(returnTo);
    const cartForRedirect = includeCartCategoryParams ? cart : undefined;
    const isAuth0Handoff = Boolean(searchParams?.get(AUTH0_ISSUER_QUERY_PARAM));

    const startLogin = useCallback(async () => {
        setHasError(false);

        try {
            await loginWithRedirect(
                getAuth0LoginRedirectOptions({
                    returnTo,
                    replaceHistory: true,
                    authorizationParams: {
                        ...authorizationParams,
                        ...(requireCredentials ? { prompt: 'login' } : {}),
                    },
                    cart: cartForRedirect,
                    // Keep the checkout interstitial source; do not infer it from returnTo
                    // or leftover session storage from a skipped `/sign-in` visit.
                    loginMethod,
                    isInStoreAutoship,
                    flowEntry,
                })
            );
        } catch (loginError) {
            // Without this the user is left on a dead "Redirecting..." screen.
            setHasError(true);
            logSentryError(loginError, {
                tags: {
                    component: 'Auth0LoginRedirect',
                    action: 'loginWithRedirect',
                    severity: 'HIGH',
                },
            });
        }
    }, [
        authorizationParams,
        cartForRedirect,
        flowEntry,
        isInStoreAutoship,
        loginMethod,
        loginWithRedirect,
        requireCredentials,
        returnTo,
    ]);

    useEffect(() => {
        // `startLogin` writes a flow entry before Auth0 navigates away. A re-run must
        // not read it back as a storefront origin and send the user Home.
        if (hasRedirected.current) {
            return;
        }

        if (
            restoreAuth0HistoryIfNeeded({
                searchParams,
                router,
                hasRedirected,
            })
        ) {
            return;
        }

        if (isLoading) {
            return;
        }

        const isAuth0HistoryHandoff = isAuth0InitiatedBackNavigation(searchParams);

        // Voluntary sign-in while a session already exists: just continue to returnTo.
        // Inactivity re-auth must not take this path, it has to prove credentials first.
        if (
            skipAuthenticatedAuth0Login({
                isAuthenticated,
                requireCredentials,
                isAuth0HistoryHandoff,
                returnTo,
                pathname,
                router,
                isInStoreAutoship,
                hasRedirected,
            })
        ) {
            return;
        }

        // A leftover SDK error must not block inactivity re-auth or an Auth0 Back
        // handoff (e.g. revoked session after password reset): start Universal Login
        // instead of the Oops page.
        if (error && !requireCredentials && !isAuth0HistoryHandoff) {
            throw error;
        }

        if (includeCartCategoryParams && isCartLoading) {
            return;
        }

        hasRedirected.current = true;
        void startLogin();
    }, [
        error,
        includeCartCategoryParams,
        isAuthenticated,
        isCartLoading,
        isInStoreAutoship,
        isLoading,
        pathname,
        requireCredentials,
        returnTo,
        router,
        searchParams,
        startLogin,
    ]);

    if (hasError) {
        return (
            <LayoutContainer data-testid="auth0-login-redirect-error">
                <TextPassage>We could not start secure sign in.</TextPassage>
                <Button text="Try again" onClick={() => void startLogin()} />
            </LayoutContainer>
        );
    }

    if (isAuth0Handoff) {
        return (
            <output
                className="auth0-login-redirect auth0-login-redirect--handoff"
                data-testid="auth0-login-redirect-handoff"
                aria-label="Loading"
                aria-live="polite"
                aria-busy="true"
            >
                <LoadingIndicator iconSize="4xl" data-testid="auth0-login-redirect-handoff-loading" />
            </output>
        );
    }

    return (
        <output className="auth0-login-redirect" data-testid="auth0-login-redirect" aria-live="polite">
            <div className="auth0-login-redirect__text">
                <Heading tagName="h2">Hang in there!</Heading>
                <Heading tagName="h4" className="auth0-login-redirect__subtext">
                    {message}
                </Heading>
            </div>
            <div className="auth0-login-redirect__loading-indicator">
                <LoadingIndicator iconSize="4xl" data-testid="auth0-login-redirect-loading" />
            </div>
        </output>
    );
}
